import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Radio, AlertTriangle, Shield, MessageSquare, Search,
  MapPin, Bell, BellOff, Zap, Clock, Activity, Volume2
} from 'lucide-react';
import { BACKEND_URL, API } from '../../utils/apiConfig';

const SEVERITY_STYLES = {
  critical: { bg: '#991b1b', border: '#dc2626', text: '#fca5a5', icon: AlertTriangle, label: 'CRITICAL' },
  warning:  { bg: '#92400e', border: '#f59e0b', text: '#fde68a', icon: Zap, label: 'WARNING' },
  info:     { bg: '#1e3a5f', border: '#3b82f6', text: '#93c5fd', icon: Activity, label: 'INFO' },
};

const EVENT_ICONS = {
  dialog_turn: MessageSquare,
  ride_search_executed: Search,
  emergency: AlertTriangle,
  emergency_resolved: Shield,
  ride_offer_executed: MapPin,
  state_reset: Clock,
  fallback: MessageSquare,
  safety_checkin: Shield,
  safety_checkin_missed: AlertTriangle,
  ride_monitoring_started: Radio,
  ride_monitoring_stopped: Radio,
};

function formatTime(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
}

function formatEventData(event) {
  const data = typeof event.data === 'string' ? JSON.parse(event.data) : (event.data || {});
  const type = event.event_type;

  if (type === 'dialog_turn') {
    const intent = data.intent || '?';
    const text = data.text || '';
    const turn = data.turn || 0;
    const missing = data.missing?.length || 0;
    return `Turn #${turn} | Intent: ${intent} | "${text.slice(0, 60)}${text.length > 60 ? '...' : ''}"${missing > 0 ? ` | ${missing} fehlend` : ''}`;
  }
  if (type === 'ride_search_executed') {
    return `${data.from_city || '?'} → ${data.to_city || '?'} | ${data.results_count || 0} Treffer`;
  }
  if (type === 'emergency') {
    const trigger = data.trigger || 'keyword';
    return `ALARM ${trigger === 'code_word' ? 'CODE-WORT' : 'KEYWORD'} | Tracking: ${data.tracking_interval || '?'}s | Stealth: ${data.stealth_mode ? 'ON' : 'OFF'}`;
  }
  if (type === 'emergency_resolved') {
    return `Notfall aufgeloest | Ride: ${data.ride_id || '?'}`;
  }
  if (type === 'state_reset') return 'Dialog-State zurueckgesetzt';

  return JSON.stringify(data).slice(0, 120);
}

export const AdminGuardianFeedTab = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [sseConnected, setSseConnected] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [stats, setStats] = useState({ dialogTurns: 0, emergencies: 0, searches: 0 });
  const feedRef = useRef(null);
  const eventSourceRef = useRef(null);
  const hasPermissionRef = useRef(false);

  // Load historical events on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/orchestrator/events?count=100`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        const sorted = (data.events || []).reverse();
        setEvents(sorted);
        // Count stats from history
        let turns = 0, emergencies = 0, searches = 0;
        sorted.forEach(e => {
          if (e.event_type === 'dialog_turn') turns++;
          if (e.event_type === 'emergency') emergencies++;
          if (e.event_type === 'ride_search_executed') searches++;
        });
        setStats({ dialogTurns: turns, emergencies, searches });
      })
      .catch(() => {});
  }, []);

  // SSE connection
  useEffect(() => {
    const url = `${API}/orchestrator/events/stream`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);

    es.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        if (event.type === 'connected') {
          setSseConnected(true);
          return;
        }
        // Parse nested data
        if (typeof event.data === 'string') {
          try { event.data = JSON.parse(event.data); } catch {}
        }

        setEvents(prev => [...prev.slice(-199), event]);

        // Update live stats
        setStats(prev => ({
          dialogTurns: prev.dialogTurns + (event.event_type === 'dialog_turn' ? 1 : 0),
          emergencies: prev.emergencies + (event.event_type === 'emergency' ? 1 : 0),
          searches: prev.searches + (event.event_type === 'ride_search_executed' ? 1 : 0),
        }));

        // Browser notification for code-word events
        if (event.event_type === 'emergency' && event.data?.trigger === 'code_word') {
          fireNotification(event);
        }
      } catch {}
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setSseConnected(false);
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [events]);

  // Request notification permission
  const toggleNotifications = useCallback(async () => {
    if (notificationsEnabled) {
      setNotificationsEnabled(false);
      hasPermissionRef.current = false;
      return;
    }
    if (!('Notification' in window)) {
      alert('Browser unterstuetzt keine Benachrichtigungen');
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      setNotificationsEnabled(true);
      hasPermissionRef.current = true;
      new Notification('Guardian Feed aktiv', {
        body: 'Du erhaeltst Benachrichtigungen bei Code-Wort Alarm.',
        icon: '/favicon.ico',
        tag: 'guardian-test',
      });
    } else {
      alert('Benachrichtigungs-Berechtigung abgelehnt');
    }
  }, [notificationsEnabled]);

  const fireNotification = useCallback((event) => {
    if (!hasPermissionRef.current) return;
    const userId = event.user_id || 'Unbekannt';
    new Notification('CODE-WORT ALARM', {
      body: `User ${userId} hat das Codewort ausgeloest!\nTracking: ${event.data?.tracking_interval || 15}s | Stealth: ON`,
      icon: '/favicon.ico',
      tag: `guardian-alarm-${event.event_id}`,
      requireInteraction: true,
    });
  }, []);

  const severityOf = (event) => {
    if (event.severity === 'critical' || event.event_type === 'emergency') return 'critical';
    if (event.severity === 'warning' || event.event_type?.includes('missed')) return 'warning';
    return 'info';
  };

  return (
    <div data-testid="admin-guardian-feed-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: 12,
        padding: '16px 20px', border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Radio size={20} style={{ color: sseConnected ? '#22c55e' : '#ef4444' }} />
          <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 16 }}>
            Live Guardian Feed
          </span>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: sseConnected ? '#22c55e' : '#ef4444',
            animation: sseConnected ? 'pulse 2s infinite' : 'none',
            display: 'inline-block',
          }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>
            {sseConnected ? 'Verbunden' : 'Getrennt'}
          </span>
        </div>

        <button
          data-testid="guardian-notifications-toggle"
          onClick={toggleNotifications}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: notificationsEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${notificationsEnabled ? '#22c55e' : '#475569'}`,
            borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
            color: notificationsEnabled ? '#22c55e' : '#94a3b8', fontSize: 13, fontWeight: 500,
          }}
        >
          {notificationsEnabled ? <Bell size={15} /> : <BellOff size={15} />}
          {notificationsEnabled ? 'Push AN' : 'Push AUS'}
        </button>
      </div>

      {/* Live stat counters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
        {[
          { label: 'Dialog-Turns', value: stats.dialogTurns, color: '#3b82f6', Icon: MessageSquare },
          { label: 'Emergencies', value: stats.emergencies, color: '#ef4444', Icon: AlertTriangle },
          { label: 'Ride-Searches', value: stats.searches, color: '#22c55e', Icon: Search },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} style={{
            background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10,
            padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={18} style={{ color }} />
            </div>
            <div>
              <div style={{ color: '#e2e8f0', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{value}</div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Event feed */}
      <div
        ref={feedRef}
        data-testid="guardian-event-feed"
        style={{
          background: '#0a0a14', border: '1px solid #1e293b', borderRadius: 12,
          maxHeight: 520, overflowY: 'auto', padding: 12,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}
      >
        {events.length === 0 && (
          <div style={{ color: '#475569', textAlign: 'center', padding: 40, fontSize: 14 }}>
            Warte auf Events... Starte den Voice Orb oder sende eine Nachricht an den Orchestrator.
          </div>
        )}
        {events.map((event, i) => {
          const sev = severityOf(event);
          const style = SEVERITY_STYLES[sev];
          const Icon = EVENT_ICONS[event.event_type] || Activity;
          const isCodeWord = event.event_type === 'emergency' && event.data?.trigger === 'code_word';

          return (
            <div
              key={event.event_id || `ev-${i}`}
              data-testid={`guardian-event-${event.event_type}`}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: style.bg, border: `1px solid ${style.border}`,
                borderRadius: 8, padding: '10px 14px',
                animation: isCodeWord ? 'flashRed 1s ease 3' : undefined,
              }}
            >
              <Icon size={16} style={{ color: style.text, marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                    color: style.text, textTransform: 'uppercase',
                    background: `${style.border}30`, padding: '2px 6px', borderRadius: 4,
                  }}>
                    {event.event_type?.replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: 10, color: '#64748b' }}>
                    {formatTime(event.timestamp)}
                  </span>
                  <span style={{ fontSize: 10, color: '#475569' }}>
                    {event.user_id?.slice(0, 8)}
                  </span>
                  {isCodeWord && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#fff',
                      background: '#dc2626', padding: '2px 8px', borderRadius: 4,
                      animation: 'pulse 1s infinite',
                    }}>
                      CODE-WORT
                    </span>
                  )}
                </div>
                <div style={{ color: style.text, fontSize: 12, marginTop: 4, opacity: 0.9, lineHeight: 1.4 }}>
                  {formatEventData(event)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes flashRed {
          0%, 100% { box-shadow: 0 0 0 transparent; }
          50% { box-shadow: 0 0 20px rgba(220,38,38,0.6); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};
